import { useState } from 'react'
import { API_BASE } from '../lib/api'
import '../styles/setup.css'

interface TestResult {
    success: boolean
    message: string
    serverVersion?: string
    database?: string
    tableCount?: number
}

export default function SetupWizard() {
    const [step, setStep] = useState(0)
    const [legacyConn, setLegacyConn] = useState('')
    const [appConn, setAppConn] = useState(
        'Host=localhost;Port=5432;Database=puresoft_modern;Username=postgres;Password=postgres'
    )
    const [superAdminEmail, setSuperAdminEmail] = useState('')
    const [legacyResult, setLegacyResult] = useState<TestResult | null>(null)
    const [appResult, setAppResult] = useState<TestResult | null>(null)
    const [testing, setTesting] = useState(false)
    const [saving, setSaving] = useState(false)
    const [complete, setComplete] = useState(false)

    const testLegacy = async () => {
        setTesting(true)
        setLegacyResult(null)
        try {
            const res = await fetch(API_BASE + '/api/setup/test-legacy-db', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ connectionString: legacyConn })
            })
            const data = await res.json()
            setLegacyResult(data)
        } catch {
            setLegacyResult({ success: false, message: 'Network error — is the API running?' })
        }
        setTesting(false)
    }

    const testApp = async () => {
        setTesting(true)
        setAppResult(null)
        try {
            const res = await fetch(API_BASE + '/api/setup/test-app-db', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ connectionString: appConn })
            })
            const data = await res.json()
            setAppResult(data)
        } catch {
            setAppResult({ success: false, message: 'Network error — is the API running?' })
        }
        setTesting(false)
    }

    const completeSetup = async () => {
        setSaving(true)
        try {
            const res = await fetch(API_BASE + '/api/setup/complete', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    legacyConnectionString: legacyConn,
                    appConnectionString: appConn,
                    superAdminEmail
                })
            })
            const data = await res.json()
            if (data.success) {
                setComplete(true)
            }
        } catch {
            // Handle error
        }
        setSaving(false)
    }

    if (complete) {
        return (
            <div className="setup-wizard">
                <div className="setup-card">
                    <div className="setup-complete">
                        <div className="check-icon">✓</div>
                        <h2>Setup Complete</h2>
                        <p>
                            Configuration saved and encrypted. Restart the API server to apply
                            the new database connections.
                        </p>
                        <div className="setup-actions" style={{ justifyContent: 'center', marginTop: '2rem' }}>
                            <button className="btn btn-primary" onClick={() => window.location.href = '/'}>
                                Go to Dashboard
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        )
    }

    return (
        <div className="setup-wizard">
            <div className="setup-card">
                <h1>Setup Wizard</h1>
                <p className="subtitle">
                    Configure database connections and create the first administrator account.
                </p>

                {/* Step indicators */}
                <div className="setup-steps">
                    {[0, 1, 2].map(i => (
                        <div
                            key={i}
                            className={`setup-step ${i === step ? 'active' : ''} ${i < step ? 'complete' : ''}`}
                        />
                    ))}
                </div>

                {/* Step 0: Legacy MSSQL */}
                {step === 0 && (
                    <>
                        <h2 style={{ fontSize: 'var(--text-lg)', marginBottom: 'var(--space-2)' }}>
                            Legacy Database (MSSQL)
                        </h2>
                        <p style={{ fontSize: 'var(--text-sm)', color: 'var(--color-text-secondary)', marginBottom: 'var(--space-6)' }}>
                            Connect to the existing POS/Inventory MSSQL database. This will be read-only.
                        </p>

                        <div className="form-group">
                            <label htmlFor="legacy-conn">Connection String</label>
                            <textarea
                                id="legacy-conn"
                                value={legacyConn}
                                onChange={e => setLegacyConn(e.target.value)}
                                placeholder="Server=myserver;Database=RetailDB;User Id=readonly;Password=***;TrustServerCertificate=True"
                                rows={3}
                            />
                            <p className="hint">
                                Use a read-only SQL login for safety. The system enforces read-only access regardless.
                            </p>
                        </div>

                        {legacyResult && (
                            <div className={`test-result ${legacyResult.success ? 'success' : 'error'}`}>
                                <span className="icon">{legacyResult.success ? '✓' : '✗'}</span>
                                <div>
                                    <strong>{legacyResult.success ? 'Connected!' : 'Failed'}</strong>
                                    <br />
                                    {legacyResult.message}
                                    {legacyResult.success && legacyResult.serverVersion && (
                                        <>
                                            <br />
                                            SQL Server: {legacyResult.serverVersion} · {legacyResult.tableCount} tables
                                        </>
                                    )}
                                </div>
                            </div>
                        )}

                        <div className="setup-actions">
                            <button
                                className="btn btn-secondary"
                                onClick={testLegacy}
                                disabled={!legacyConn.trim() || testing}
                            >
                                {testing ? <span className="spinner" /> : null}
                                Test Connection
                            </button>
                            <button
                                className="btn btn-primary"
                                onClick={() => setStep(1)}
                                disabled={!legacyResult?.success}
                            >
                                Next →
                            </button>
                        </div>
                    </>
                )}

                {/* Step 1: App PostgreSQL */}
                {step === 1 && (
                    <>
                        <h2 style={{ fontSize: 'var(--text-lg)', marginBottom: 'var(--space-2)' }}>
                            App Database (PostgreSQL)
                        </h2>
                        <p style={{ fontSize: 'var(--text-sm)', color: 'var(--color-text-secondary)', marginBottom: 'var(--space-6)' }}>
                            Configure the new PostgreSQL database for users, reports, and settings.
                        </p>

                        <div className="form-group">
                            <label htmlFor="app-conn">Connection String</label>
                            <textarea
                                id="app-conn"
                                value={appConn}
                                onChange={e => setAppConn(e.target.value)}
                                rows={2}
                            />
                            <p className="hint">
                                The database will be automatically created if it doesn't exist.
                            </p>
                        </div>

                        {appResult && (
                            <div className={`test-result ${appResult.success ? 'success' : 'error'}`}>
                                <span className="icon">{appResult.success ? '✓' : '✗'}</span>
                                <div>{appResult.message}</div>
                            </div>
                        )}

                        <div className="setup-actions">
                            <button className="btn btn-secondary" onClick={() => setStep(0)}>
                                ← Back
                            </button>
                            <button
                                className="btn btn-secondary"
                                onClick={testApp}
                                disabled={!appConn.trim() || testing}
                            >
                                {testing ? <span className="spinner" /> : null}
                                Test Connection
                            </button>
                            <button
                                className="btn btn-primary"
                                onClick={() => setStep(2)}
                                disabled={!appResult?.success}
                            >
                                Next →
                            </button>
                        </div>
                    </>
                )}

                {/* Step 2: SuperAdmin */}
                {step === 2 && (
                    <>
                        <h2 style={{ fontSize: 'var(--text-lg)', marginBottom: 'var(--space-2)' }}>
                            SuperAdmin Account
                        </h2>
                        <p style={{ fontSize: 'var(--text-sm)', color: 'var(--color-text-secondary)', marginBottom: 'var(--space-6)' }}>
                            The first user to sign in with this email will be assigned the SuperAdmin role.
                        </p>

                        <div className="form-group">
                            <label htmlFor="admin-email">Google Workspace Email</label>
                            <input
                                id="admin-email"
                                type="email"
                                value={superAdminEmail}
                                onChange={e => setSuperAdminEmail(e.target.value)}
                                placeholder="admin@vpo.ca"
                            />
                            <p className="hint">
                                Must be a @vpo.ca Google Workspace email address.
                            </p>
                        </div>

                        <div className="setup-actions">
                            <button className="btn btn-secondary" onClick={() => setStep(1)}>
                                ← Back
                            </button>
                            <button
                                className="btn btn-success"
                                onClick={completeSetup}
                                disabled={
                                    !superAdminEmail.endsWith('@vpo.ca') ||
                                    saving
                                }
                            >
                                {saving ? <span className="spinner" /> : null}
                                Complete Setup
                            </button>
                        </div>
                    </>
                )}
            </div>
        </div>
    )
}
